/**
 * PaymentStreakBadge — Phase 85.2
 *
 * "6 EMIs on track · 3y 1m to freedom" — turns the payment ledger into
 * something that feels like progress. Counts only CONFIRMED payments (the
 * backend computes the streak in lib/liability_reconcile/streak.py), so it can
 * never overstate what actually happened.
 */
import React from 'react';
import { Flame, Target } from 'lucide-react';

function horizon(months) {
  if (!months || months <= 0) return null;
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y && m) return `${y}y ${m}m`;
  if (y) return `${y}y`;
  return `${m}m`;
}

export function PaymentStreakBadge({ streak }) {
  if (!streak) return null;
  const { streak_months: months = 0, payments_counted: count = 0 } = streak;
  const toFreedom = horizon(streak.months_to_freedom);

  // Nothing confirmed yet — the empty payment-activity card already explains
  // what to do, so stay quiet rather than showing a zero.
  if (!count) return null;

  const headline = months > 0
    ? `${months} EMI${months === 1 ? '' : 's'} on track`
    : `${count} payment${count === 1 ? '' : 's'} linked`;

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border bg-card px-4 py-3 shadow-sm"
      data-testid="liab-payment-streak"
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-charcoal">
        <Flame className="h-4 w-4 text-evergreen" aria-hidden="true" />
        <span data-testid="liab-streak-headline">{headline}</span>
      </span>
      {toFreedom && (
        <>
          <span className="text-muted-foreground/50" aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Target className="h-4 w-4" aria-hidden="true" />
            <span data-testid="liab-streak-freedom">{toFreedom} to freedom</span>
          </span>
        </>
      )}
      {months > 0 && streak.emi_amount ? (
        <span className="text-[11px] text-muted-foreground/80 basis-full">
          Consecutive months with a confirmed payment covering your EMI.
        </span>
      ) : (
        <span className="text-[11px] text-muted-foreground/80 basis-full">
          Confirm more payments to build a streak.
        </span>
      )}
    </div>
  );
}

export default PaymentStreakBadge;
